'use client';

import { useRouter } from 'next/navigation';
import { Wifi, Database, Brain, MonitorSmartphone, ArrowRight } from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: Wifi,
    title: '데이터 수집',
    description: 'ESP32 IoT 센서가 농장의 온도, 습도, 토양 상태 등을 실시간으로 측정합니다.',
    color: 'from-blue-500 to-blue-600',
    iconBg: 'bg-blue-500',
  },
  {
    number: '02',
    icon: Database,
    title: '데이터 저장',
    description: 'Spring Boot 서버가 센서 데이터를 PostgreSQL 데이터베이스에 안전하게 저장합니다.',
    color: 'from-purple-500 to-purple-600',
    iconBg: 'bg-purple-500',
  },
  {
    number: '03',
    icon: Brain,
    title: 'AI 분석',
    description: '인공지능이 수집된 데이터를 분석하여 최적의 환경 조건과 관리 방안을 도출합니다.',
    color: 'from-pink-500 to-pink-600',
    iconBg: 'bg-pink-500',
  },
  {
    number: '04',
    icon: MonitorSmartphone,
    title: '자동 제어',
    description: 'Next.js 대시보드에서 확인하고, 액추에이터가 환경을 자동으로 최적화합니다.',
    color: 'from-green-500 to-green-600',
    iconBg: 'bg-green-500',
  },
];

export default function HowItWorksSection() {
  const router = useRouter();

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 섹션 헤더 */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">IFMS는 어떻게 작동하나요?</h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            IoT부터 AI까지, 4단계의 스마트한 프로세스로 여러분의 농장을 관리합니다.
          </p>
        </div>

        {/* 프로세스 스텝 */}
        <div className="relative">
          {/* 연결선 (데스크톱) */}
          <div
            className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 via-pink-500 to-green-500 transform -translate-y-1/2"
            style={{ top: '80px' }}
          ></div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                {/* 카드 */}
                <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  {/* 숫자 배지 */}
                  <div
                    className={`absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br ${step.color} rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg`}
                  >
                    {step.number}
                  </div>

                  {/* 아이콘 */}
                  <div className={`inline-flex p-4 ${step.iconBg} rounded-xl mb-4`}>
                    <step.icon className="w-8 h-8 text-white" />
                  </div>

                  {/* 제목 */}
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{step.title}</h3>

                  {/* 설명 */}
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{step.description}</p>
                </div>

                {/* 화살표 (모바일/태블릿) */}
                {index < steps.length - 1 && (
                  <div className="lg:hidden flex justify-center my-4">
                    <ArrowRight className="w-6 h-6 text-gray-400 transform rotate-90" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA 버튼들 */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-10">
          <button
            onClick={() => router.push('/howitworks')} // 이동할 경로
            className="group px-12 py-4 bg-green-500  text-white font-bold rounded-lg shadow-xl hover:shadow-2xl transition-all duration-200 transform hover:-translate-y-1 flex items-center gap-2"
          >
            자세히
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        {/* 기술 스택 */}
        <div className="mt-20">
          <h3 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-8">검증된 기술로 구축된 안정적인 시스템</h3>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {[
              { name: 'Next.js', color: 'bg-black dark:bg-white' },
              { name: 'React', color: 'bg-blue-500' },
              { name: 'TypeScript', color: 'bg-blue-600' },
              { name: 'Spring Boot', color: 'bg-green-600' },
              { name: 'PostgreSQL', color: 'bg-blue-700' },
              { name: 'ESP32', color: 'bg-red-600' },
            ].map((tech, index) => (
              <div
                key={index}
                className="group relative bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-center"
              >
                <div
                  className={`w-12 h-12 ${tech.color} rounded-lg mx-auto mb-3 flex items-center justify-center text-white font-bold text-sm`}
                >
                  {tech.name.charAt(0)}
                </div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{tech.name}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
